import { useEffect, useState } from 'react';
import { loadPDF, PDFDocument } from '../utils/pdfjs';

interface PDFViewerProps {
  file: File;
}

interface ThumbnailData {
  pageNumber: number;
  canvas: HTMLCanvasElement;
  loaded: boolean;
}

export const PDFViewer = ({ file }: PDFViewerProps) => {
  const [pdf, setPdf] = useState<PDFDocument | null>(null);
  const [thumbnails, setThumbnails] = useState<ThumbnailData[]>([]);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFile();
  }, [file]);

  const loadFile = async () => {
    try {
      const pdfDoc = await loadPDF(file);
      setPdf(pdfDoc);
      
      // Create thumbnails
      const thumbs: ThumbnailData[] = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        thumbs.push({
          pageNumber: i,
          canvas: document.createElement('canvas'),
          loaded: false
        });
      }
      setThumbnails(thumbs);
      setSelectedPages(new Set(Array.from({length: pdfDoc.numPages}, (_, i) => i + 1)));
      setLoading(false)
      let lastYieldTime = Date.now()
      
      // Render all thumbnails
      for (const thumb of thumbs) {
        const page = await pdfDoc.getPage(thumb.pageNumber);
        const originalViewport = page.getViewport({ scale: 1.0 });
        const desiredWidth = 200
        const height = desiredWidth / (originalViewport.width / originalViewport.height)
        const scale = desiredWidth / originalViewport.width
        const viewport = page.getViewport({scale})
        
        thumb.canvas.width = desiredWidth;
        thumb.canvas.height = height;
        
        const context = thumb.canvas.getContext('2d');
        if (!context) continue
        await page.render({ canvasContext: context, viewport }).promise;
        thumb.loaded = true;
        setThumbnails(prev => prev.map(t =>
          t.pageNumber === thumb.pageNumber ? {...t, loaded: true} : t
        )); 
        const now = Date.now()
        if (now - lastYieldTime >= 500) {
          await new Promise(resolve => setTimeout(resolve, 10));
          lastYieldTime = now
        }
      }
        
    } catch (error) {
      console.error('Error loading PDF:', error);
      setLoading(false);
    }
  };

  const togglePage = (pageNumber: number) => {
    setSelectedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageNumber)) {
        newSet.delete(pageNumber);
      } else {
        newSet.add(pageNumber);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedPages(new Set(thumbnails.map(t => t.pageNumber)));
  };

  const clearAll = () => {
    setSelectedPages(new Set());
  };

  const saveSelected = async () => {
    if (!pdf || selectedPages.size === 0) return;
    
    try {
      const pagesToKeep = Array.from(selectedPages).sort((a, b) => a - b);
      await pdf.keepPages(pagesToKeep);
      const pdfBytes = await pdf.saveDocument();
      
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${file.name.replace('.pdf', '')}_${pagesToKeep.length}pages.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error saving PDF:', error);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading PDF...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">{file.name}</h3>
          <p className="text-sm text-gray-600">{selectedPages.size} of {thumbnails.length} pages selected</p>
        </div>
        <div className="space-x-2">
          <button onClick={selectAll} className="btn-secondary text-sm">Select All</button>
          <button onClick={clearAll} className="btn-secondary text-sm">Clear All</button>
          <button onClick={saveSelected} disabled={selectedPages.size === 0} className="btn-primary text-sm">
            Save {selectedPages.size} Pages
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {thumbnails.map((thumbnail) => {
          const isSelected = selectedPages.has(thumbnail.pageNumber);
          
          return (
            <div 
              key={thumbnail.pageNumber}
              className={`
                relative cursor-pointer border-2 rounded-lg p-2 transition-colors
                ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
              `}
              onClick={() => togglePage(thumbnail.pageNumber)}
            >
              {thumbnail.loaded ? (
                <canvas
                  ref={(el) => {
                    if (el && thumbnail.canvas) {
                      el.width = thumbnail.canvas.width;
                      el.height = thumbnail.canvas.height;
                      const ctx = el.getContext('2d');
                      if (ctx) ctx.drawImage(thumbnail.canvas, 0, 0);
                    }
                  }}
                  className="w-full h-auto"
                />
              ) : (
                <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center">
                  Loading...
                </div>
              )}
              
              <div className="text-center mt-2">
                <span className="text-xs font-medium">Page {thumbnail.pageNumber}</span>
              </div>
              
              {isSelected && (
                <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
